/**
 * Rename Mutations Hook
 *
 * Provides mutation hooks for renaming workspace items (tables, linked tables).
 * Uses optimistic updates for instant UI feedback.
 *
 * @module features/workspace/hooks/useRenameMutations
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { WorkspaceItem } from '@loader/shared-types';

import { useRpc, extractErrorMessage } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Updates item name in workspace items cache
 */
function updateWorkspaceItemName(
  items: WorkspaceItem[] | undefined,
  id: string,
  name: string
): WorkspaceItem[] | undefined {
  if (!items) return undefined;
  return items.map((item) => (item.id === id ? { ...item, name } : item));
}

// ============================================================================
// TABLE RENAME MUTATION
// ============================================================================

/**
 * Hook to rename a table
 *
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation
 * - Error handling with rollback
 */
export function useRenameTableMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private.tables[':id'].$patch({
        param: { id },
        json: { name },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to rename table'));
      }

      return res.json();
    },
    onMutate: async ({ id, name }) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.items.all });

      // Snapshot previous values for rollback
      const previousItems = queryClient.getQueriesData<WorkspaceItem[]>({
        queryKey: queryKeys.organization.items.all,
      });

      // Optimistically update all workspace items caches
      queryClient.setQueriesData<WorkspaceItem[]>(
        { queryKey: queryKeys.organization.items.all },
        (old) => updateWorkspaceItemName(old, id, name)
      );

      return { previousItems };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousItems) {
        for (const [queryKey, data] of context.previousItems) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: (_, __, variables) => {
      // Invalidate to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tables.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.tables });
    },
  });
}

// ============================================================================
// LINKED TABLE RENAME MUTATION
// ============================================================================

/**
 * Hook to rename a linked table
 *
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic cache invalidation
 * - Error handling with rollback
 */
export function useRenameLinkedTableMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      // @ts-expect-error - Hono RPC type inference limitation for nested routes
      const res = await rpc.api.private['linked-tables'][':id'].$patch({
        param: { id },
        json: { name },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to rename linked table'));
      }

      return res.json();
    },
    onMutate: async ({ id, name }) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.organization.items.all });

      // Snapshot previous values for rollback
      const previousItems = queryClient.getQueriesData<WorkspaceItem[]>({
        queryKey: queryKeys.organization.items.all,
      });

      // Optimistically update all workspace items caches
      queryClient.setQueriesData<WorkspaceItem[]>(
        { queryKey: queryKeys.organization.items.all },
        (old) => updateWorkspaceItemName(old, id, name)
      );

      return { previousItems };
    },
    onError: (_error, _variables, context) => {
      // Rollback on error
      if (context?.previousItems) {
        for (const [queryKey, data] of context.previousItems) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: (_, __, variables) => {
      // Invalidate to refetch fresh data
      void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.organization.items.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataGrid.linkedTables });
    },
  });
}
